import { useSelector } from "react-redux";
import useLegend from "../Clients/useLegend";
import { selectStatistic } from "../StateManagement/statisticSourceSlice";

const NUM_TICKS = 5;
const TICK_END_X = 155;
const LABEL_X = 160;

const formatValue = (value, preUnit, postUnit) => {
  let formatted;
  if (value >= 1_000_000) {
    formatted = (value / 1_000_000).toFixed(1) + "M";
  } else if (value >= 10_000) {
    formatted = Math.round(value / 1_000) + "K";
  } else if (value >= 1_000) {
    formatted = (value / 1_000).toFixed(1) + "K";
  } else {
    formatted = Math.round(value).toString();
  }
  return preUnit + formatted + postUnit;
};

// Arrow outline edges at a given y.
// Tip: left edge (150,60)→(100,0), right edge (100,0)→(150,60). Shaft: x=75/125.
const getArrowLeftEdge = (y) => {
  if (y >= 60) return 75;
  return 100 - (5 * y) / 6;
};

const getArrowRightEdge = (y) => {
  if (y >= 60) return 125;
  return 100 + (5 * y) / 6;
};

const MapLegendArrow = () => {
  const [data] = useLegend();
  const statistic = useSelector(selectStatistic);

  if (!data) {
    return null;
  }

  const sortedData = [...data].sort((a, b) => b.value - a.value);
  const N = sortedData.length;
  const colors = sortedData.map((d) => d.color);

  // Find scale break: the largest value gap between consecutive stops (ascending).
  // This is where the outlier yellow zone begins.
  const sortedAsc = [...data].sort((a, b) => a.value - b.value);
  let breakAscIndex = 1;
  let maxJump = 0;
  for (let i = 1; i < sortedAsc.length; i++) {
    const jump = sortedAsc[i].value - sortedAsc[i - 1].value;
    if (jump > maxJump) {
      maxJump = jump;
      breakAscIndex = i;
    }
  }
  // Convert break to y coordinate (biased toward the lower stop to make the
  // yellow tip section more visible).
  const breakDescHigh = N - 1 - breakAscIndex; // outlier side (near tip)
  const breakDescLow = N - breakAscIndex;       // normal side (below)
  const yBreak =
    ((breakDescHigh * 0.25 + breakDescLow * 0.75) / (N - 1)) * 200;

  // Approach B: 5 ticks at evenly-spaced stop indices.
  // y position is proportional to stop index so ticks align with the gradient stops.
  const ticks = Array.from({ length: NUM_TICKS }, (_, i) => {
    const stopIndex = Math.round((i / (NUM_TICKS - 1)) * (N - 1));
    const y = (stopIndex / (N - 1)) * 200;
    const value = sortedData[stopIndex].value;
    const tickStartX = getArrowRightEdge(y) + 2;
    return { y, value, tickStartX };
  });

  return (
    <svg viewBox="20 -12 190 222">
      <defs>
        <linearGradient id="legendGradient" gradientTransform="rotate(90)">
          {colors.map((color, index) => (
            <stop
              key={index}
              offset={`${(index / (colors.length - 1)) * 100}%`}
              style={{ stopColor: color, stopOpacity: 1 }}
            />
          ))}
        </linearGradient>
      </defs>

      <path
        d="M50,60 L100,0 L150,60 L125,60 L125,200 L75,200 L75,60 Z"
        fill="url(#legendGradient)"
      />

      {/* Scale-break marker: single white line spanning the arrow width at the
          outlier boundary. */}
      <line
        x1={getArrowLeftEdge(yBreak + 3)}
        y1={yBreak + 3}
        x2={getArrowRightEdge(yBreak + 3)}
        y2={yBreak + 3}
        stroke="white"
        strokeWidth="2"
      />

      {ticks.map(({ y, value, tickStartX }, i) => (
        <g key={i}>
          <line
            x1={tickStartX}
            y1={y}
            x2={TICK_END_X}
            y2={y}
            stroke="black"
            strokeWidth="1.5"
          />
          <text
            x={LABEL_X}
            y={y + 5}
            fill="black"
            fontSize="14"
          >
            {formatValue(value, statistic.preUnit, statistic.postUnit)}
          </text>
        </g>
      ))}
    </svg>
  );
};

export default MapLegendArrow;
